<template>
  <BaseDialog
    width="520px"
    :show-text="false"
    :text-align="'left'"
    :footer-align="'flex-end'"
    :title="$t('carriers.import.assign.Title', { policy: row.policyNumber })"
    :confirm-button-text="$t('carriers.import.assign.Confirm')"
    :cancel-button-text="$t('adminTitles.cancel')"
    :confirm-disable="!profileId || saving"
    :confirm-loading="saving"
    @confirm="save"
    @close="$emit('close')"
  >
    <p class="assign-client__lead">
      {{ $t('carriers.import.assign.Lead', { name: row.clientName || '—' }) }}
    </p>
    <SearchDropdown
      :placeholder="$t('carriers.import.assign.Search')"
      :options="options"
      :loading="loading"
      :selected-option="profileId"
      @select="(option) => { profileId = option.id; }"
      @search="search"
    />
    <p class="assign-client__hint">{{ $t('carriers.import.assign.CreateHint') }}</p>
    <p v-if="error" class="assign-client__error">{{ error }}</p>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import SearchDropdown from '@/views/RenovaDashboard/components/ui-kit/Dropdowns/SearchDropdown';
import { searchUsers } from '@/store/modules/users';
import { apiError } from '@/utils/carrierImport';

/**
 * Name the client for a staged policy the carrier gave no RFC, e-mail or phone
 * for (an AXA Vida policy, say). Without one the row is set aside and can never
 * be applied; with one it becomes a new policy for that person.
 *
 * Searches the clients this broker already has, the same search the claim form
 * uses. Nothing is created here: a client who is not in Renova yet is added
 * from Clients first, then found here.
 */
export default {
  name: 'AssignClientDialog',
  components: { BaseDialog, SearchDropdown },
  props: {
    row: { type: Object, required: true },
  },
  emits: ['close', 'assigned'],
  data() {
    return {
      options: [],
      loading: false,
      profileId: null,
      saving: false,
      error: '',
    };
  },
  created() {
    // Start from the name the carrier printed: most of the time that is who it is.
    this.search(this.row.clientName || '');
  },
  methods: {
    async search(query) {
      this.loading = true;
      try {
        const res = await searchUsers(query, 50);
        this.options = (res.data.items || [])
          .map((user) => ({
            id: user.personProfileId,
            name: user.lastName || user.firstName
              ? `${user.lastName || ''} ${user.firstName || ''}`.trim()
              : user.email || user.phone,
          }))
          .filter((o) => o.id && o.name);
      } catch (e) {
        this.options = [];
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!this.profileId) return;
      this.saving = true;
      this.error = '';
      try {
        await this.$store.dispatch('carrierImportAssignSnapshotClient', {
          id: this.row.id,
          personProfileId: this.profileId,
        });
        this.$emit('assigned', this.row.id);
      } catch (e) {
        this.error = apiError(e, this.$t('adminTitles.someError'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.assign-client__lead {
  margin: 0 0 16px;
  color: var(--text-color, #333);
}

.assign-client__hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #8a8a8a;
}

.assign-client__error {
  margin: 12px 0 0;
  color: #e5484d;
}
</style>
